import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import AuthProvider from '@/auth/AuthProvider'
import LocalAuthProvider from '@/auth/LocalAuthContext'
import { DemoDataProvider } from '@/context/DemoDataContext'
import ProtectedRoute from '@/auth/ProtectedRoute'
import InviteGate from '@/auth/InviteGate'
import Layout from '@/components/shared/Layout'
import LiveBookGate from '@/components/shared/LiveBookGate'
import NotFound from '@/components/shared/NotFound'
import { AppErrorBoundary } from '@/components/shared/ErrorBoundary'
import CommandCenter from '@/pages/CommandCenter'
import DBSearch from '@/pages/DBSearch'
import Pipeline from '@/pages/PipelineIntelligence'
import Intake from '@/pages/Intake'
import Screening from '@/pages/Screening'
import Underwriting from '@/pages/Underwriting'
import Committee from '@/pages/Committee'
import Closing from '@/pages/Closing'
import Surveillance from '@/pages/Surveillance'
import AssetManagement from '@/pages/AssetManagement'
import Valuation from '@/pages/Valuation'
import MarketIntelligence from '@/pages/MarketIntelligence'
import MarketNews from '@/pages/MarketNews'
import Reports from '@/pages/reporting/ReportingCommandCenter'
import CRMDatabase from '@/pages/CRMDatabase'
import UserProfile from '@/pages/UserProfile'
import LoanComparison from '@/pages/LoanComparison'
import PortfolioAnalytics from '@/pages/PortfolioAnalytics'
import TermSheet from '@/pages/TermSheet'
import HedgeDesk from '@/pages/HedgeDesk'
import DocumentVault from '@/pages/DocumentVault'
import InvestorPortal from '@/pages/InvestorPortal'
import BorrowerPortal from '@/pages/BorrowerPortal'
import BIDashboard from '@/pages/BIDashboard'
import CompDatabase from '@/pages/CompDatabase'
import WorkflowAutomation from '@/pages/WorkflowAutomation'
import GoldenDealReconciliation from '@/pages/GoldenDealReconciliation'
import GoldenDealTrace from '@/pages/GoldenDealTrace'
import CreditBox from '@/pages/CreditBox'
import ModelTemplateGovernance from '@/pages/ModelTemplateGovernance'
import LegalComplianceDashboard from '@/pages/dashboards/LegalComplianceDashboard'
import UnderwritingManagerDashboard from '@/pages/dashboards/UnderwritingManagerDashboard'
import AssetManagerDashboard from '@/pages/dashboards/AssetManagerDashboard'
import CeoDashboard from '@/pages/dashboards/CeoDashboard'
import PeopleDashboard from '@/pages/dashboards/PeopleDashboard'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { ROLE_LANDING, WORKFLOW_META, WORKFLOW_ORDER } from '@/config/workflowModules'
import type { ModuleGroup } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'

/**
 * Where a session lands after sign-in.
 *
 * Replaces a hardcoded Navigate to /command-center. The fallback chain keeps
 * that behavior intact for every role that existed before the dashboards:
 * licensed role landing (only the four new roles declare one) → /command-center
 * → the first module the license actually includes. The last step matters for a
 * role scoped to one board, which would otherwise land on a denial screen and
 * have to be bounced out of it by Layout.
 *
 * The role landing is itself permission-checked: a general_counsel whose
 * license omits the legal board must fall through, not land on the
 * not-licensed screen.
 *
 * Group order here mirrors Layout's `firstAvailable` deliberately — the
 * dashboards lead WORKFLOW_ORDER because that is the nav order, and scanning
 * it raw would relocate where every pre-existing license lands.
 */
const LANDING_GROUP_ORDER: ModuleGroup[] = ['pre-closing', 'post-closing', 'tools', 'dashboards']

function RoleLanding() {
  const { localUser } = useLocalAuth()

  const permissions = localUser?.permissions
  const canView = (id: PlatformModuleId) => {
    if (localUser?.is_super_admin) return true
    if (!permissions) return true
    const actions = permissions[id]
    return actions ? actions.includes('view') : true
  }

  const roleModule = localUser?.role ? ROLE_LANDING[localUser.role] : undefined
  const firstLicensed = LANDING_GROUP_ORDER.flatMap((group) =>
    WORKFLOW_ORDER.filter((id) => WORKFLOW_META[id].group === group),
  ).find(canView)

  const target =
    roleModule && canView(roleModule)
      ? roleModule
      : canView('command-center')
        ? 'command-center'
        : (firstLicensed ?? 'command-center')

  return <Navigate to={WORKFLOW_META[target].route} replace />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
    },
  },
})

export default function App() {
  return (
    <AppErrorBoundary>
    <InviteGate>
    <LocalAuthProvider>
      <AuthProvider>
        <DemoDataProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ProtectedRoute>
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<RoleLanding />} />
                <Route path="dashboards/legal" element={<LegalComplianceDashboard />} />
                <Route path="dashboards/underwriting" element={<UnderwritingManagerDashboard />} />
                <Route path="dashboards/asset" element={<AssetManagerDashboard />} />
                <Route path="dashboards/ceo" element={<CeoDashboard />} />
                <Route path="dashboards/people" element={<PeopleDashboard />} />
                <Route path="command-center" element={<LiveBookGate moduleId="command-center"><CommandCenter /></LiveBookGate>} />
                <Route path="db-search" element={<DBSearch />} />
                <Route path="pipeline" element={<LiveBookGate moduleId="pipeline"><Pipeline /></LiveBookGate>} />
                <Route path="intake" element={<Intake />} />
                <Route path="screening" element={<LiveBookGate moduleId="screening"><Screening /></LiveBookGate>} />
                <Route path="underwriting" element={<LiveBookGate moduleId="underwriting"><Underwriting /></LiveBookGate>} />
                <Route path="committee" element={<LiveBookGate moduleId="committee"><Committee /></LiveBookGate>} />
                <Route path="closing" element={<LiveBookGate moduleId="closing"><Closing /></LiveBookGate>} />
                <Route path="surveillance" element={<LiveBookGate moduleId="surveillance"><Surveillance /></LiveBookGate>} />
                <Route path="asset-management" element={<LiveBookGate moduleId="asset-management"><AssetManagement /></LiveBookGate>} />
                <Route path="valuation" element={<LiveBookGate moduleId="valuation"><Valuation /></LiveBookGate>} />
                <Route path="market-intel" element={<MarketIntelligence />} />
                <Route path="market-news" element={<MarketNews />} />
                <Route path="reports" element={<LiveBookGate moduleId="reports"><Reports /></LiveBookGate>} />
                <Route path="crm" element={<CRMDatabase />} />
                <Route path="loan-comparison" element={<LiveBookGate moduleId="loan-comparison"><LoanComparison /></LiveBookGate>} />
                <Route path="portfolio-analytics" element={<LiveBookGate moduleId="portfolio-analytics"><PortfolioAnalytics /></LiveBookGate>} />
                <Route path="term-sheet" element={<LiveBookGate moduleId="term-sheet"><TermSheet /></LiveBookGate>} />
                <Route path="hedge-desk" element={<HedgeDesk />} />
                <Route path="documents" element={<DocumentVault />} />
                <Route path="investor-portal" element={<InvestorPortal />} />
                <Route path="borrower-portal" element={<LiveBookGate moduleId="borrower-portal"><BorrowerPortal /></LiveBookGate>} />
                <Route path="borrower-portal/live/:dealId" element={<LiveBookGate moduleId="borrower-portal"><BorrowerPortal /></LiveBookGate>} />
                <Route path="bi-dashboard" element={<BIDashboard />} />
                <Route path="comp-database" element={<LiveBookGate moduleId="comp-database"><CompDatabase /></LiveBookGate>} />
                <Route path="automation" element={<WorkflowAutomation />} />
                <Route path="golden-deal-reconciliation" element={<GoldenDealReconciliation />} />
                <Route path="golden-deal-reconciliation/:model" element={<GoldenDealTrace />} />
                <Route path="credit-box" element={<CreditBox />} />
                <Route path="model-templates" element={<ModelTemplateGovernance />} />
                <Route path="settings" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </ProtectedRoute>
          </BrowserRouter>
        </QueryClientProvider>
        </DemoDataProvider>
      </AuthProvider>
    </LocalAuthProvider>
    </InviteGate>
    </AppErrorBoundary>
  )
}
